import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { WaiterService } from '../../_service/waiter.service';
import { CommonModule, Location } from '@angular/common';
import Swal from 'sweetalert2';
import { PusherService } from '../../_service/pusher.service';
import { FormsModule } from '@angular/forms';  // Add this import

@Component({
  selector: 'app-payment-method',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule],
  templateUrl: './payment-method.component.html',
  styleUrl: './payment-method.component.scss'
})
export class orderPaymentMethodComponent implements OnDestroy {
  id: string | null;
  orderDetails: any;
  private posPaymentTimer: any;
  showCashInput = false;
  showGeneralTransferInput = false;
  cashAmount = 0;
  showPosInput = false;
  posAmount = 0;
  private posPaymentSubscription: any;
  showTransferInput = false;
  transferAmount = 0;
  generalTransferAmount = 0;
  remainingAmount = 0;

  // Loading states
  isLoading = true;
  isProcessingPayment = false;
  isInitializingPOS = false;
  isCalculatingAmount = false;

  // Payment method states
  selectedPaymentMethod: string | null = null;
  paymentMethods = [
    {
      id: 'general_transfer',
      name: 'General Transfer',
      icon: 'icons/bank-transfer.svg',
      description: 'Quick bank transfer'
    },
    {
      id: 'cash',
      name: 'Cash',
      icon: 'icons/cash.svg',
      description: 'Cash payment'
    },
    {
      id: 'transfer',
      name: 'Dynamic Bank Transfer',
      icon: 'icons/bank-transfer.svg',
      description: 'Automated bank transfer'
    },
    {
      id: 'pos',
      name: 'Card (POS)',
      icon: 'icons/card.svg',
      description: 'Credit/Debit card payment'
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private service: WaiterService,
    private router: Router,
    private location: Location,
    private pusherService: PusherService
  ) {
    this.id = route.snapshot.paramMap.get('id')
  }

  ngOnInit(): void {
    var user = JSON.parse(localStorage.getItem('user') || '{}');
    this.initializeFlutterBridge();
    this.waitForFlutterBridge();

    // Store the subscription reference
    this.posPaymentSubscription = this.pusherService.posPaymentEvent$.subscribe((data: any) => {
      if (data.type === "pos_payment") {
        this.isProcessingPayment = false;
        Swal.close(); // Close the loader modal
        clearTimeout(this.posPaymentTimer); // Clear the timeout

        if (data.data.payment_status === 'pending') {
          Swal.fire('Sorry', 'Transaction Declined', 'error');
        } else if (data.data.payment_status === 'partial_payment') {
          Swal.fire({
            title: 'Yo!',
            text: 'Transaction Approved',
            icon: 'success',
            confirmButtonText: 'OK'
          }).then((result) => {
            if (result.isConfirmed) {
              this.router.navigateByUrl(`/orders/${this.id}`);
            }
          });
        } else if (data.data.payment_status === 'approved') {
          Swal.fire({
            title: 'Yo!',
            text: 'Transaction Approved',
            icon: 'success',
            confirmButtonText: 'OK'
          }).then((result) => {
            if (result.isConfirmed) {
              this.router.navigateByUrl('/');
            }
          });
        }
      }
    });

    // Get order details and calculate amounts
    if (this.id) {
      this.service.getOrderDetails(this.id).subscribe({
        next: (res: any) => {
          this.orderDetails = res;
          this.calculateRemainingAmount();
        },
        error: (err) => {
          console.error('Error fetching order details:', err);
          Swal.fire('Error', 'Failed to load order details', 'error');
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    } else {
      this.isLoading = false;
    }
  }

  goBack() {
    this.location.back();
  }
  private waitForFlutterBridge() {
    // Create a custom event for when Flutter bridge is ready
    window.addEventListener('flutterBridgeReady', () => {
      console.log('Flutter bridge is ready');
    });

    // Initialize bridge with checking mechanism
    window.FlutterBridge = {
      postMessage: (message: string) => {
        if (window.FlutterChannel?.postMessage) {
          window.FlutterChannel.postMessage(message);
        } else {
          console.log('Flutter bridge not initialized:', message);
          // Fallback to Pusher
          try {
            const data = JSON.parse(message);
            console.log(data);
            // this.pusherService.trigger('waiter-notification', data);
          } catch (e) {
            console.error('Error parsing message:', e);
          }
        }
      }
    };
  }
  private initializeFlutterBridge() {
    // Define the interface for communicating with Flutter
    window.FlutterBridge = {
      postMessage: (message: string) => {
        // This will be overridden by Flutter's JavaScript channel
        console.log('Flutter bridge not initialized:', message);
      }
    };
  }
  showPosLoader() {
    Swal.fire({
      title: 'Processing Payment',
      html: 'Please wait while we process your payment...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Set timeout for 1 minute
    this.posPaymentTimer = setTimeout(() => {
      Swal.close();
      Swal.fire({
        title: 'Timeout',
        text: 'Payment processing took too long. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }, 60000); // 60 seconds
  }

  selectCashPayment() {
    this.selectedPaymentMethod = 'cash';
    this.showCashInput = true;
    this.cashAmount = this.remainingAmount;
    this.hideOtherInputs('cash');
  }

  selectGeneralTransferPayment() {
    this.selectedPaymentMethod = 'general_transfer';
    this.showGeneralTransferInput = true;
    this.generalTransferAmount = this.remainingAmount;
    this.hideOtherInputs('general_transfer');
  }

  selectPosPayment() {
    this.selectedPaymentMethod = 'pos';
    this.showPosInput = true;
    this.posAmount = this.remainingAmount;
    this.hideOtherInputs('pos');
  }

  selectTransferPayment() {
    this.selectedPaymentMethod = 'transfer';
    this.showTransferInput = true;
    this.transferAmount = this.remainingAmount;
    this.hideOtherInputs('transfer');
  }

  private hideOtherInputs(currentMethod: string) {
    if (currentMethod !== 'cash') this.showCashInput = false;
    if (currentMethod !== 'general_transfer') this.showGeneralTransferInput = false;
    if (currentMethod !== 'pos') this.showPosInput = false;
    if (currentMethod !== 'transfer') this.showTransferInput = false;
  }

  initiatePayment(method: string, amount?: number) {
    if ((method === 'cash' || method === 'transfer') && (!amount || amount <= 0)) {
      Swal.fire('Error', 'Please enter a valid amount', 'error');
      return;
    }

    this.isProcessingPayment = true;

    if (method === 'pos') {
      this.isInitializingPOS = true;
      Swal.fire({
        title: 'Are you sure?',
        text: `Please Insert Card`,
        iconHtml: '<i class="fas fa-credit-card"></i>',
        customClass: {
          icon: 'no-border'
        },
        showCancelButton: true,
        confirmButtonText: 'Yes, proceed',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          const posInitData = {
            type: 'pos_init',
            data: {
              uid: this.id,
              amount: amount?.toString(),
              order_items: this.orderDetails.order_items
            }
          };

          // Try sending through bridge with retry mechanism
          this.sendThroughBridge(posInitData);

          // Show loading state
          this.showPosLoader();
        } else {
          this.isProcessingPayment = false;
          this.isInitializingPOS = false;
        }
      });
    } else {
      this.processPayment(method, amount);
    }
  }

  processPayment(payment_method: any, amount?: number) {
    const data = {
      order_id: this.id,
      payment_method: payment_method,
      amount: amount
    };

    this.service.initiatePayment(data).subscribe({
      next: (res: any) => {
        // Stop loading immediately when response is received
        this.isProcessingPayment = false;
        this.isInitializingPOS = false;

        if (res.payment_method === "cash") {
          if (res.payment_status === 'pending') {
            Swal.fire('', 'Payment has not been confirmed', 'warning');
          } else if (res.payment_status === 'partial_payment') {
            Swal.fire({
              title: 'Yo!',
              text: 'Transaction Approved',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then((result) => {
              if (result.isConfirmed) {
                this.router.navigateByUrl(`/orders/${this.id}`);
              }
            });
          } else if (res.payment_status === 'approved') {
            Swal.fire({
              title: 'Yo!',
              text: 'Transaction Approved',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then((result) => {
              if (result.isConfirmed) {
                this.router.navigateByUrl('/');
              }
            });
          }
        } else if (res.payment_method === "general_transfer") {
          Swal.fire({
            title: 'Yo!',
            text: 'Transaction Approved',
            icon: 'success',
            confirmButtonText: 'OK'
          }).then((result) => {
            if (result.isConfirmed) {
              this.router.navigateByUrl(`/orders/${this.id}`);
            }
          });
        } else if (res.payment_method === "transfer") {
          Swal.fire({
            title: 'Yo!',
            text: 'Bank account created successfully',
            icon: 'success',
            confirmButtonText: 'OK'
          }).then((result) => {
            if (result.isConfirmed) {
              this.router.navigateByUrl(`/orders/${this.id}`);
            }
          });
        }
      },
      error: (err) => {
        // Stop loading on error as well
        this.isProcessingPayment = false;
        this.isInitializingPOS = false;

        Swal.fire({
          title: "Failed!",
          text: err.error.message,
          icon: "error"
        });
      },
      complete: () => {
        // Ensure loading is stopped in complete as well (backup)
        this.isProcessingPayment = false;
        this.isInitializingPOS = false;
      }
    });
  }

  private sendThroughBridge(data: any, retries = 3) {
    try {
      window.FlutterBridge.postMessage(JSON.stringify(data));
    } catch (e) {
      console.error('Failed to communicate with Flutter:', e);
      if (retries > 0) {
        // Retry after a short delay
        setTimeout(() => {
          this.sendThroughBridge(data, retries - 1);
        }, 500);
      } else {
        // Fallback to Pusher after all retries fail
        // this.pusherService.trigger('waiter-notification', data);
      }
    }
  }

  calculateRemainingAmount() {
    const totalAmount = this.orderDetails?.total_amount || 0;
    const totalReceived = this.orderDetails?.transactionLog?.reduce((sum: number, transaction: any) => {
      return sum + (parseFloat(transaction.amount) || 0);
    }, 0) || 0;
    this.remainingAmount = totalAmount - totalReceived;
  }

  ngOnDestroy() {
    // Clean up timer when component is destroyed
    if (this.posPaymentTimer) {
      clearTimeout(this.posPaymentTimer);
    }

    // Unsubscribe from POS payment events
    if (this.posPaymentSubscription) {
      this.posPaymentSubscription.unsubscribe();
    }
  }
}

declare global {
  interface Window {
    FlutterBridge: {
      postMessage(message: string): void;
    };
    FlutterChannel?: {
      postMessage(message: string): void;
    };
  }
}
